import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { PhxToastMetaData, PhxToastOptions } from './phx-toast.types';

export const PhxToastConfigDefaultValues: PhxToastOptions = {
  phxContentOrientation: 'horizontal',
  phxClosable: false,
  phxDuration: 6000,
  phxPauseOnHover: false,
  phxMaxStack: 3,
  phxHasIcon: true,
};

@Component({
  selector: 'phx-toast-container',
  template: `
    <phx-toast *ngFor="let instance of instances" [phxToastInstance]="instance" (phxToastDestroyed)="remove($event.id, $event.userAction)"></phx-toast>
  `,
  styleUrls: ['./phx-toast-container.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'phx-toast-container' },
})
export class PhxToastContainerComponent {
  config?: PhxToastOptions = PhxToastConfigDefaultValues;
  instances: Array<PhxToastMetaData> = [];

  private readonly _afterAllToastsRemoved = new Subject<void>();

  public readonly afterAllToastsRemoved = this._afterAllToastsRemoved.asObservable();

  constructor(private _cdr: ChangeDetectorRef) {}

  public create(data: PhxToastMetaData): PhxToastMetaData {
    const instance = this.onCreate(data);

    if (this.instances.length >= this.config.phxMaxStack) {
      const [oldestInstance] = this.instances;
      if (oldestInstance) {
        this.onRemove(oldestInstance, false);
      }
      this.instances = this.instances.slice(1);
    }

    this.instances = [...this.instances, instance];

    this._cdr.detectChanges();

    return instance;
  }

  public remove(id: string, userAction: boolean = false): void {
    this.instances.some((instance, index) => {
      if (instance.toastId === id) {
        this.instances.splice(index, 1);
        this.instances = [...this.instances];
        this.onRemove(instance, userAction);
        this._cdr.detectChanges();
        return true;
      }
      return false;
    });

    if (!this.instances.length) {
      this.onAllInstancesRemoved();
    }
  }

  public removeAll(): void {
    this.instances.forEach((i) => this.onRemove(i, false));
    this.instances = [];

    this._cdr.detectChanges();
    this.onAllInstancesRemoved();
  }

  private onCreate(instance: PhxToastMetaData): PhxToastMetaData {
    instance.options = this.mergeOptions(instance.options);
    instance.onClose = new Subject<boolean>();
    return instance;
  }

  private onRemove(instance: PhxToastMetaData, userAction: boolean): void {
    instance.onClose.next(userAction);
    instance.onClose.complete();
  }

  private onAllInstancesRemoved(): void {
    this._afterAllToastsRemoved.next();
    this._afterAllToastsRemoved.complete();
  }

  private mergeOptions(options?: PhxToastOptions): PhxToastOptions {
    return { ...this.config, ...(options || {}) };
  }
}
